import { supabase } from '@/integrations/supabase/client';

interface WLLSPattern {
  pattern: string;
  frequency: number;
  next_win_long: number;
  next_loss_long: number;
  next_win_short: number;
  next_loss_short: number;
  win_rate: number;
  long_rate: number;
  confidence_score: number;
}

interface Pattern {
  pattern: string;
  frequency: number;
  nextWinCount: number;
  nextLossCount: number;
  successRate: number;
  confidenceScore: number;
  lastSeenIndex: number;
}

interface RealisticPredictionResult {
  prediction: 'WIN' | 'LOSS' | 'SKIP';
  confidence: number;
  winProbability: number;
  lossProbability: number;
  usedPatterns: number;
  patternsUsed: Array<{
    pattern: string;
    frequency: number;
    successRate: number;
    confidenceScore: number;
  }>;
  skipReason?: string;
}

export class WLLSPredictionService {
  /**
   * Make realistic prediction using WLLS patterns with honest engine logic
   */
  static async makeRealisticPrediction(
    fileId: string,
    inputSequence: string,
    threshold: number = 0.52
  ): Promise<RealisticPredictionResult> {
    // Get WLLS patterns from database
    const { data: wllsPatterns, error } = await supabase
      .from('wlls_patterns')
      .select('*')
      .eq('file_id', fileId)
      .order('confidence_score', { ascending: false });

    if (error) throw error;

    if (!wllsPatterns || wllsPatterns.length === 0) {
      throw new Error('No WLLS patterns available for prediction. Please analyze patterns first.');
    }

    // Check if this is binary mode by examining pattern strings
    const isBinary = wllsPatterns.some(p => p.pattern && !p.pattern.includes('L') && !p.pattern.includes('S'));

    // Convert WLLS patterns to internal format
    const internalPatterns = wllsPatterns.map(row => this.convertWLLSPattern(row, isBinary));

    // Parse input sequence - for WLLS we expect pattern strings, not numbers
    // Convert to string if it's comma-separated numbers
    let sequence: string;
    if (inputSequence.includes(',')) {
      // Handle number sequences like "1,0,1,0"
      sequence = inputSequence.split(',').map(n => n.trim()).join('');
    } else {
      // Handle direct pattern strings like "1010" or "1L0S1L"
      sequence = inputSequence;
    }

    // Apply makeRealisticPrediction logic
    return this.applyRealisticPrediction(internalPatterns, sequence, threshold);
  }

  /**
   * Convert WLLS pattern to internal format
   */
  private static convertWLLSPattern(wllsPattern: WLLSPattern, isBinary: boolean): Pattern {
    // For binary mode, use only win/loss counts from long columns
    // For side-aware mode, combine all win/loss counts
    const nextWinCount = isBinary 
      ? wllsPattern.next_win_long
      : wllsPattern.next_win_long + wllsPattern.next_win_short;
    
    const nextLossCount = isBinary
      ? wllsPattern.next_loss_long  
      : wllsPattern.next_loss_long + wllsPattern.next_loss_short;

    return {
      pattern: wllsPattern.pattern,
      frequency: wllsPattern.frequency,
      nextWinCount,
      nextLossCount,
      successRate: wllsPattern.win_rate,
      confidenceScore: wllsPattern.confidence_score,
      lastSeenIndex: 0 // WLLS doesn't track this
    };
  }

  /**
   * Apply realistic prediction logic (ported from ManualPredictionComparisonService)
   */
  private static applyRealisticPrediction(
    patterns: Pattern[],
    inputSequence: string,
    threshold: number
  ): RealisticPredictionResult {
    const allMatchingPatterns: Pattern[] = [];

    // PHASE 1: Collect ALL matching patterns (no early termination)
    for (const pattern of patterns) {
      if (inputSequence.endsWith(pattern.pattern)) {
        allMatchingPatterns.push(pattern);
      }
    }

    // Sort patterns by quality (success rate first, then frequency, then length)
    allMatchingPatterns.sort((a, b) => {
      // Primary: Higher success rate
      if (Math.abs(a.successRate - b.successRate) > 5) {
        return b.successRate - a.successRate;
      }
      // Secondary: Higher frequency (reliability)
      if (Math.abs(a.frequency - b.frequency) > 1) {
        return b.frequency - a.frequency;
      }
      // Tertiary: Longer patterns (more specific)
      return b.pattern.length - a.pattern.length;
    });

    // Select top 8 patterns
    const matchingPatterns = allMatchingPatterns.slice(0, 8);

    // Default values
    let winProbability = 50.0;
    let lossProbability = 50.0;
    let prediction: 'WIN' | 'LOSS' | 'SKIP' = 'SKIP';
    let confidence = 0;
    let skipReason = 'No matching patterns found';

    if (matchingPatterns.length > 0) {
      // Improved weighted calculation
      let totalWeight = 0;
      let weightedWinRate = 0;
      
      for (const pattern of matchingPatterns) {
        // Enhanced confidence calculation
        const enhancedConfidence = this.calculateEnhancedConfidence(
          pattern.frequency, 
          pattern.successRate, 
          pattern.pattern.length
        );
        
        let successRate = pattern.successRate || 50;
        
        // Convert percentage to proper range
        if (successRate > 1) {
          successRate = successRate / 100;
        }
        
        successRate = Math.min(Math.max(successRate, 0), 1);
        
        totalWeight += enhancedConfidence;
        weightedWinRate += successRate * enhancedConfidence;
      }
      
      if (totalWeight > 0) {
        winProbability = (weightedWinRate / totalWeight) * 100;
        lossProbability = 100 - winProbability;
        
        // Enhanced confidence score calculation
        const avgConfidence = totalWeight / matchingPatterns.length;
        const patternCountBonus = Math.min(20, matchingPatterns.length * 2.5);
        confidence = Math.min(95, avgConfidence + patternCountBonus);
        
        // Make prediction using threshold
        const thresholdPercent = threshold * 100;
        const winThresholdPercent = thresholdPercent;
        const lossThresholdPercent = 100 - thresholdPercent;
        
        if (winProbability > winThresholdPercent) {
          prediction = 'WIN';
          skipReason = undefined;
        } else if (winProbability < lossThresholdPercent) {
          prediction = 'LOSS';
          skipReason = undefined;
        } else {
          prediction = 'SKIP';
          skipReason = `Mixed signals (${winProbability.toFixed(1)}% vs ${thresholdPercent}% threshold)`;
        }
      }
    }

    const patternsUsed = matchingPatterns.map(pattern => ({
      pattern: pattern.pattern,
      frequency: pattern.frequency,
      successRate: pattern.successRate,
      confidenceScore: this.calculateEnhancedConfidence(pattern.frequency, pattern.successRate, pattern.pattern.length)
    }));

    return {
      prediction,
      confidence,
      winProbability,
      lossProbability,
      usedPatterns: matchingPatterns.length,
      patternsUsed,
      skipReason
    };
  }

  /**
   * Enhanced confidence calculation from honest backtest
   */
  private static calculateEnhancedConfidence(frequency: number, successRate: number, patternLength: number): number {
    // Frequency weight (logarithmic scale for diminishing returns)
    const frequencyWeight = Math.min(40, 10 * Math.log10(frequency + 1));
    
    // Success rate weight (exponential for strong bias toward high success)
    const normalizedSuccessRate = Math.max(0, Math.min(100, successRate)) / 100;
    const successRateWeight = Math.pow(normalizedSuccessRate, 1.5) * 40;
    
    // Pattern length weight (longer patterns are more specific)
    const lengthWeight = Math.min(20, (patternLength - 2) * 4);
    
    // Base confidence (minimum viable pattern)
    const baseConfidence = 5;
    
    // Calculate total confidence
    const totalConfidence = baseConfidence + frequencyWeight + successRateWeight + lengthWeight;
    
    // Cap at reasonable maximum
    return Math.min(95, Math.max(5, totalConfidence));
  }
}